import { useEffect } from 'react';

/**
 * Injects a single <script type="application/ld+json" id={id}> into <head>
 * with the supplied JSON-LD payload. Removes it on unmount or when `data`
 * changes. We never render anything in the React tree because Google crawls
 * `<head>` for structured data, not the body.
 *
 * The `id` prop is required so each page's schema can be replaced cleanly
 * during client-side navigation (e.g. moving between /templates/foo and
 * /templates/bar without leaving stale FAQPage data on the page).
 */
export default function JsonLd({ id, data }) {
  useEffect(() => {
    if (typeof document === 'undefined' || !data) return undefined;
    const scriptId = `jsonld-${id}`;
    let el = document.getElementById(scriptId);
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = scriptId;
      document.head.appendChild(el);
    }
    try {
      el.textContent = JSON.stringify(data);
    } catch {
      // A circular reference or non-serialisable value should never reach
      // here, but if it does we'd rather drop the script than crash render.
      el.remove();
    }
    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, [id, data]);

  return null;
}
